import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GHLNotConnected from "@/components/GHLNotConnected";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Users, Mail, Phone, Loader2, Search } from "lucide-react";

export default function Clients() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["ghl-contacts"],
    queryFn: async () => (await api.get("/ghl/contacts?limit=50")).data,
    retry: false,
  });

  const notConnected = error?.response?.status === 400;
  const contacts = data?.contacts || (Array.isArray(data) ? data : []);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? contacts.filter((c) => {
        const name = [c.firstName, c.lastName, c.contactName].filter(Boolean).join(" ").toLowerCase();
        return name.includes(q) || (c.email || "").toLowerCase().includes(q) || (c.phone || "").toLowerCase().includes(q);
      })
    : contacts;

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" subtitle="Your counselling clients, synced from GoHighLevel. Their portal activity stays private." />

      {isLoading ? (
        <div className="flex items-center gap-3 text-navy-500"><Loader2 className="h-5 w-5 animate-spin text-gold-500" /> Loading from GoHighLevel…</div>
      ) : notConnected ? (
        <GHLNotConnected icon={Users} title="Connect to view clients" description="Link your GoHighLevel sub-account to see your client contacts here. You'll only see contact details — never their private portal activity." />
      ) : error ? (
        <Card className="rounded-2xl border-navy-900/5 bg-white p-6 text-navy-600" data-testid="ghl-error">Couldn't load clients. {error.response?.data?.detail || ""}</Card>
      ) : contacts.length === 0 ? (
        <Card className="rounded-2xl border-navy-900/5 bg-white p-10 text-center text-navy-500">No clients found.</Card>
      ) : (
        <>
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-400" />
            <Input
              data-testid="client-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients by name, email or phone..."
              className="rounded-full border-navy-100 bg-white pl-9 focus-visible:ring-gold-500"
            />
          </div>
          {filtered.length === 0 ? (
            <Card className="rounded-2xl border-navy-900/5 bg-white p-10 text-center text-navy-500" data-testid="no-client-matches">
              No clients match "{query}".
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((c, i) => (
            <Card key={c.id || i} data-testid={`client-${i}`} className="flex items-center gap-4 rounded-2xl border-navy-900/5 bg-white p-5 shadow-ambient">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold-50 font-serif text-lg font-bold text-gold-600">
                {(c.firstName || c.contactName || c.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="truncate font-semibold text-navy-900">{[c.firstName, c.lastName].filter(Boolean).join(" ") || c.contactName || "Unnamed"}</div>
                {c.email && <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-navy-500"><Mail className="h-3 w-3" /> {c.email}</div>}
                {c.phone && <div className="mt-0.5 flex items-center gap-1 text-xs text-navy-500"><Phone className="h-3 w-3" /> {c.phone}</div>}
              </div>
            </Card>
          ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
